/**
 * Batch Operations - Batch Update API for CRDT-First Architecture
 *
 * This module provides the batch update API that allows multiple CRDT operations
 * (add, update, remove, move) to be buffered and applied as a single Yjs transaction.
 * This improves performance by:
 * - Reducing the number of Store rebuilds (one instead of N)
 * - Grouping operations for atomic WebSocket transmission
 * - Supporting nested batch calls
 *
 * Extracted from crdt-bridge.js for better separation of concerns.
 * Uses a callback injection pattern to avoid circular dependencies.
 *
 * Usage:
 *   beginBatch();
 *   try {
 *     CRDTBridge.addNode(...);
 *     CRDTBridge.updateNode(...);
 *     CRDTBridge.removeNode(...);
 *   } finally {
 *     endBatch();
 *   }
 */

import { emit, Events } from "../core/events.js";
import { createLogger } from "../core/logger.js";
import { recordBatchMetric } from "./crdt-metrics.js";
import { getActiveShareId } from "./share-state.js";
import {
  DIMENSION_FIELDS,
  splitDimensionFields,
  storeLocalDimensions,
} from "./dimension-manager.js";
import {
  getSharedDocManager,
  _handleSharedDocUpdateCore,
} from "./share-lifecycle.js";
import { rebuildStoreFromCRDT } from "./store-rebuild.js";
import { USER_ORIGIN } from "./origins.js";
import { STUB_LAYOUT_FIELDS } from "./node-operations.js";
import * as StorageHealth from "./storage-health.js";

// ============================================
// Logging Setup
// ============================================
const log = createLogger("Batch Operations");

const logError = (...args) => log.error(...args);
const logWarn = (...args) => log.warn(...args);
const logInfo = (...args) => log.info(...args);
const logDebug = (...args) => log.debug(...args);

// ============================================
// Module State
// ============================================

/** When non-null, updates are buffered instead of sent immediately */
let batchBuffer = null;

/** Support nested batch calls */
let batchDepth = 0;

// ============================================
// Callback Injection Pattern
// ============================================

/**
 * Callbacks provided by crdt-bridge.js during initialization
 * @type {Object}
 */
let callbacks = {
  getDocManager: null,           // () => docManager (main doc manager)
  getStore: null,                // () => store module
  getSyncingDepth: null,         // () => syncingDepth
  incrementSyncingDepth: null,   // () => void
  decrementSyncingDepth: null,   // () => void
  // Routes a nodeId in the composited tree to its content/host doc. Used
  // so batched writes into a linked-doc subtree go to the linked doc
  // (and not to the main doc, which doesn't know those nodeIds).
  getDocManagerForLinkedNode: null, // (nodeId) => {docManager, hostManager, hostDocId, linkedDocId, translatedId, isStub}
};

/**
 * Initialize batch operations with callbacks from crdt-bridge.js
 * @param {Object} cbs - Callback functions
 */
export function initBatchOperations(cbs) {
  callbacks = { ...callbacks, ...cbs };
  logDebug("Batch operations initialized");
}

// ============================================
// Helper Functions
// ============================================

/**
 * Get the appropriate doc manager for a node. Mirrors the logic in
 * node-operations.js so batched writes follow the same routing rules
 * (active share → shared doc; composited linked-doc node → linked doc;
 * otherwise → main doc).
 * @param {string} nodeId
 * @returns {{ docManager, hostManager, shareId, linkedDocId, hostDocId, translatedId, isStub }}
 */
function getDocManagerForNode(nodeId) {
  const activeShare = getActiveShareId();
  if (activeShare) {
    const shareMgr = getSharedDocManager(activeShare);
    return {
      docManager: shareMgr, hostManager: shareMgr,
      shareId: activeShare, linkedDocId: null, hostDocId: activeShare,
      translatedId: nodeId, isStub: false,
    };
  }
  const getLinkedDM = callbacks.getDocManagerForLinkedNode;
  const linked = getLinkedDM ? getLinkedDM(nodeId) : null;
  if (linked && (linked.linkedDocId || linked.hostDocId)) {
    return {
      docManager: linked.docManager,
      hostManager: linked.hostManager,
      shareId: null,
      linkedDocId: linked.linkedDocId,
      hostDocId: linked.hostDocId,
      translatedId: linked.translatedId || nodeId,
      isStub: !!linked.isStub,
    };
  }
  const dm = callbacks.getDocManager();
  return {
    docManager: dm, hostManager: dm,
    shareId: null, linkedDocId: null, hostDocId: null,
    translatedId: nodeId, isStub: false,
  };
}

/**
 * Execute a single batch operation on a doc manager
 * MD11 Fix: Added error handling to track operation success/failure
 * @param {YjsDocManager} dm - The doc manager
 * @param {Object} op - The operation to execute
 * @returns {boolean} True if operation succeeded
 */
function executeBatchOp(dm, op) {
  try {
    switch (op.type) {
      case 'add':
        dm.addNode(op.node, op.parentId, op.index);
        break;
      case 'update': {
        let updates = op.updates;
        // Intercept notes: redirect to Y.XmlFragment if in yxml-v1 mode
        if ('notes' in updates && typeof updates.notes === 'string' && dm.metadata?.get('notesFormat') === 'yxml-v1') {
          dm.setNodeNotesFromMarkdown(op.nodeId, updates.notes);
          const { notes: _removed, ...rest } = updates;
          updates = rest;
        }
        if (Object.keys(updates).length > 0) {
          dm.updateNode(op.nodeId, updates);
        }
        break;
      }
      case 'remove':
        dm.removeNode(op.nodeId);
        break;
      case 'move':
        dm.moveNode(op.nodeId, op.writeParentId ?? op.newParentId, op.index);
        break;
      default:
        logWarn(`Unknown batch operation type: ${op.type}`);
        op.success = false;
        op.error = new Error(`Unknown operation type: ${op.type}`);
        return false;
    }
    op.success = true;
    return true;
  } catch (error) {
    logError(`Batch operation failed: ${op.type}`, error);
    op.success = false;
    op.error = error;
    return false;
  }
}

// ============================================
// Public API
// ============================================

/**
 * Begin batching updates. All CRDT operations (add, update, remove, move)
 * will be buffered until endBatch() is called, then applied as a single
 * Yjs transaction per doc manager.
 *
 * Supports nested calls - only the outermost endBatch() flushes.
 */
export function beginBatch() {
  StorageHealth.assertHealthy();
  batchDepth++;
  if (batchBuffer === null) {
    batchBuffer = [];
    logDebug("Batch mode started");
  }
}

/**
 * End batching and flush all buffered operations as a single transaction.
 * Groups operations by doc manager (main vs shared) and applies each group atomically.
 * Events are emitted after Store rebuild to ensure listeners see consistent state.
 *
 * CR5/CR7 Fix: Increments syncingDepth during flush to prevent CRDT observer from
 * triggering double Store rebuilds. We manually rebuild once after all ops complete.
 *
 * Phase 1.2 Fix: Improved defensive checks for inconsistent batch state.
 */
export function endBatch() {
  // Phase 1.2 Fix: Defensive check for inconsistent state
  if (batchDepth <= 0) {
    logWarn("endBatch called with no active batch (depth was", batchDepth, ")");
    // Reset to consistent state
    batchDepth = 0;
    batchBuffer = null;
    return;
  }

  try {
    StorageHealth.assertHealthy();
  } catch (err) {
    batchDepth = 0;
    batchBuffer = null;
    throw err;
  }

  batchDepth--;
  if (batchDepth > 0) {
    // Nested batch - don't flush yet
    return;
  }

  // Phase 1.2 Fix: Capture and clear buffer atomically BEFORE processing
  // This ensures even if an error occurs, we exit batch mode cleanly
  const operations = batchBuffer;
  batchBuffer = null;

  if (!operations || operations.length === 0) {
    return;
  }

  // F5.6: Start timing batch operation
  const batchStartTime = performance.now();

  logInfo("Flushing batch with", operations.length, "operations");

  const store = callbacks.getStore();
  const docManager = callbacks.getDocManager();

  // CR5/CR7 Fix: Increment syncingDepth to prevent CRDT observer from double-processing
  // We'll manually rebuild Store once after all ops are applied
  callbacks.incrementSyncingDepth();
  let decremented = false;

  try {
    // Group operations by target doc manager.
    const mainOps = [];
    const sharedOpsMap = new Map(); // shareId -> ops[]
    const linkedOpsMap = new Map(); // linkedDocId -> { docManager, ops[] }

    // Collect events to emit after Store rebuild
    const pendingEvents = [];

    for (const op of operations) {
      // Determine the nodeId to route the operation
      const routingId = op.type === 'add' ? op.parentId : op.nodeId;
      const routing = getDocManagerForNode(routingId);
      const { shareId, linkedDocId, isStub, translatedId, hostDocId, hostManager } = routing;
      // Removing or moving a link edits the map that holds the link, which
      // is loaded even when the linked map is not (no access, loading).
      const onLinkItself = isStub && (op.type === 'remove' || op.type === 'move');
      const targetDocManager = onLinkItself ? hostManager : routing.docManager;

      if (!targetDocManager) {
        logWarn(`Batch ${op.type} skipped: target map not loaded for ${routingId}`);
        op.success = false;
        op.error = new Error('The target map is not loaded');
        continue;
      }

      // Phase 4.4 Fix + linked-doc extension: block cross-boundary moves.
      // The axis is: main / each shareId / each linkedDocId. Moves across
      // these boundaries are not supported in batch mode.
      if (op.type === 'move' && op.newParentId) {
        const dstRouting = getDocManagerForNode(op.newParentId);
        // `linkedDocId` on a stub points at the target doc (where its
        // children live), so the same expression works for stub and
        // non-stub parents alike.
        const srcDoc = shareId || linkedDocId || 'main';
        const dstDoc = dstRouting.shareId || dstRouting.linkedDocId || 'main';
        if (srcDoc !== dstDoc) {
          logError(`Cross-boundary move not supported in batch: ${op.nodeId} from ${srcDoc} to ${dstDoc}`);
          op.success = false;
          op.error = new Error('Cross-boundary moves are not supported in batch mode. Call CRDTBridge.moveNode() directly instead.');
          continue;
        }
        // Moving under a link: its children live in the linked map, under
        // the linked root's id (as the single move translates it). Without
        // this the node got a parent id that doesn't exist there (orphan).
        // The event keeps the id seen in the map (op.newParentId).
        if (dstRouting.isStub) op.writeParentId = dstRouting.translatedId;
      }

      // Translate add-under-stub: the parentId for linked targets must be
      // the linked root's id, not the stub's main-doc id.
      if (op.type === 'add' && isStub) {
        op.parentId = translatedId;
      }
      // Translate in-place writes for stub content (handled per-op type below).

      // G1-H2 / linked-doc extension: dimension fields stay LOCAL for any
      // doc other than the user's own main (shared or linked). The viewer
      // may render at a different size than the owner, and cached values
      // must not cross-contaminate.
      const needsLocalDims = op.type === 'update'
        && (shareId || (linkedDocId && !isStub));
      if (needsLocalDims) {
        const { dimensionUpdates, syncableUpdates } = splitDimensionFields(op.updates);
        if (Object.keys(dimensionUpdates).length > 0) {
          storeLocalDimensions(op.nodeId, dimensionUpdates);
          store._updateNodeDirect(op.nodeId, dimensionUpdates);
          logDebug(`Stored local dimensions for ${shareId ? "shared" : "linked"} node: ${op.nodeId}`, dimensionUpdates);
        }
        if (Object.keys(syncableUpdates).length === 0) {
          logDebug(`Skipping dimension-only CRDT update: ${op.nodeId}`);
          continue;
        }
        op.updates = syncableUpdates;
      }

      if (shareId) {
        if (!sharedOpsMap.has(shareId)) sharedOpsMap.set(shareId, []);
        sharedOpsMap.get(shareId).push(op);
      } else if (linkedDocId && !isStub) {
        // Write lands in the linked doc (node lives there, content edits).
        if (!linkedOpsMap.has(linkedDocId)) {
          linkedOpsMap.set(linkedDocId, { docManager: targetDocManager, ops: [] });
        }
        linkedOpsMap.get(linkedDocId).ops.push(op);
      } else if (isStub && op.type === 'add') {
        // add-child under a stub → linked doc. Parent was already translated above.
        if (!linkedOpsMap.has(linkedDocId)) {
          linkedOpsMap.set(linkedDocId, { docManager: targetDocManager, ops: [] });
        }
        linkedOpsMap.get(linkedDocId).ops.push(op);
      } else if (isStub && op.type === 'update') {
        // Stub updates split: layout fields live on the stub (main doc),
        // content fields live on the linked root in the linked doc. The
        // split matches node-operations.updateNode interactive path.
        const layoutUpdates = {};
        const contentUpdates = {};
        for (const [k, v] of Object.entries(op.updates)) {
          if (STUB_LAYOUT_FIELDS.has(k)) layoutUpdates[k] = v;
          else contentUpdates[k] = v;
        }
        if (Object.keys(layoutUpdates).length > 0) {
          if (hostDocId && hostManager) {
            // A link inside a linked map: its layout lives in that map.
            if (!linkedOpsMap.has(hostDocId)) linkedOpsMap.set(hostDocId, { docManager: hostManager, ops: [] });
            linkedOpsMap.get(hostDocId).ops.push({ ...op, updates: layoutUpdates });
          } else {
            mainOps.push({ ...op, updates: layoutUpdates });
          }
        }
        if (Object.keys(contentUpdates).length > 0) {
          if (!linkedOpsMap.has(linkedDocId)) {
            linkedOpsMap.set(linkedDocId, { docManager: targetDocManager, ops: [] });
          }
          linkedOpsMap.get(linkedDocId).ops.push({
            ...op, updates: contentUpdates, nodeId: translatedId,
          });
        }
      } else if (onLinkItself && hostDocId) {
        // Move/remove of a link inside a linked map → that map.
        if (!linkedOpsMap.has(hostDocId)) linkedOpsMap.set(hostDocId, { docManager: hostManager, ops: [] });
        linkedOpsMap.get(hostDocId).ops.push(op);
      } else {
        // Stub move/remove or plain main-doc op → main.
        mainOps.push(op);
      }

      // Collect event to emit later (with op reference for success checking)
      switch (op.type) {
        case 'add':
          pendingEvents.push({ event: Events.NODE_ADDED, payload: { parentId: op.parentId, node: op.node, index: op.index }, op });
          break;
        case 'update':
          // F4.2: Use captured oldValues from buffered operation
          pendingEvents.push({ event: Events.NODE_UPDATED, payload: { nodeId: op.nodeId, updates: op.updates, oldValues: op.oldValues || {} }, op });
          break;
        case 'remove':
          pendingEvents.push({ event: Events.NODE_REMOVED, payload: { nodeId: op.nodeId, parentId: op.parentId, node: op.node }, op });
          break;
        case 'move':
          pendingEvents.push({ event: Events.NODE_MOVED, payload: { nodeId: op.nodeId, oldParentId: op.oldParentId, newParentId: op.newParentId, index: op.index }, op });
          break;
      }
    }

    // Apply batched operations to main doc in a single transaction
    if (mainOps.length > 0 && docManager) {
      logDebug("Batch applying to main doc:", mainOps.length, "operations");
      docManager.doc.transact(() => {
        for (const op of mainOps) {
          executeBatchOp(docManager, op);
        }
      }, USER_ORIGIN);
    }

    // Apply batched operations to each shared doc in single transactions
    for (const [shareId, ops] of sharedOpsMap) {
      const sharedDocManager = getSharedDocManager(shareId);
      if (sharedDocManager && ops.length > 0) {
        logDebug("Batch applying to shared doc:", shareId, ops.length, "operations");
        sharedDocManager.doc.transact(() => {
          for (const op of ops) {
            executeBatchOp(sharedDocManager, op);
          }
        }, USER_ORIGIN);
      }
    }

    // Apply batched operations to each linked doc in single transactions.
    // Updates linked-doc content when it's embedded via a top-level stub in
    // the current main view.
    for (const [linkedDocId, entry] of linkedOpsMap) {
      if (entry.docManager && entry.ops.length > 0) {
        logDebug("Batch applying to linked doc:", linkedDocId, entry.ops.length, "operations");
        entry.docManager.doc.transact(() => {
          for (const op of entry.ops) {
            executeBatchOp(entry.docManager, op);
          }
        }, USER_ORIGIN);
      }
    }

    // CR5/CR7 Fix: Decrement syncingDepth BEFORE rebuild so observer doesn't re-trigger
    // but we've already applied all CRDT changes in the transaction
    callbacks.decrementSyncingDepth();
    decremented = true;

    // C2 Fix (PLAN18): Track which documents were affected and rebuild appropriately
    const activeShareId = getActiveShareId();
    const mainDocAffected = mainOps.length > 0;
    const linkedDocAffected = linkedOpsMap.size > 0;
    const affectedShareIds = new Set(sharedOpsMap.keys());

    // Rebuild main doc if it was affected OR if any linked doc it composites
    // changed — in main view the composited tree reflects both sources.
    if ((mainDocAffected || linkedDocAffected) && !activeShareId) {
      rebuildStoreFromCRDT('batch');
    }

    // G1-H1 Fix (PLAN19): Always process affected shared docs, not just when viewing them
    // When viewing main doc, shared doc changes should merge into main Store
    for (const shareId of affectedShareIds) {
      const sharedDocManager = getSharedDocManager(shareId);
      if (sharedDocManager) {
        if (activeShareId === shareId) {
          // We're currently viewing this share - trigger its rebuild
          _handleSharedDocUpdateCore(shareId);
        } else if (!activeShareId) {
          // G1-H1 Fix: We're viewing main doc but modified a shared doc
          // Need to merge the subtree changes into the main Store
          _handleSharedDocUpdateCore(shareId);
        }
      }
    }

    // If we're viewing a shared doc but it wasn't affected by this batch,
    // and main doc was affected, we still need to rebuild main doc for consistency
    if (mainDocAffected && activeShareId && !affectedShareIds.has(activeShareId)) {
      rebuildStoreFromCRDT('batch');
    }

    // MD11 Fix: Only emit events for successful operations
    for (const { event, payload, op } of pendingEvents) {
      if (op.success !== false) {
        emit(event, payload);
      }
    }

    // Also emit DATA_CHANGED for any listeners that depend on it
    emit(Events.DATA_CHANGED, { source: "batch" });

    // F5.6: Record batch timing
    const batchElapsed = performance.now() - batchStartTime;
    recordBatchMetric(batchElapsed);
    logDebug(`Batch completed: ${operations.length} ops in ${batchElapsed.toFixed(1)}ms`);

  } finally {
    // Ensure syncingDepth is always decremented even on error (but only if not already done)
    if (!decremented) {
      callbacks.decrementSyncingDepth();
    }
  }
}

/**
 * Check if currently in batch mode
 * @returns {boolean}
 */
export function isBatching() {
  return batchBuffer !== null;
}

/**
 * Get the batch buffer (used by node operations to check batch mode)
 * @returns {Array|null} The batch buffer array, or null if not batching
 */
export function getBatchBuffer() {
  return batchBuffer;
}

/**
 * Phase 1.2 Fix: Emergency reset of batch state
 * Use this to recover from inconsistent batch state (e.g., after an unhandled error
 * that left batch mode active). This discards any buffered operations.
 *
 * WARNING: Only use for error recovery. Normal code should use beginBatch/endBatch.
 */
export function resetBatchState() {
  if (batchDepth > 0 || batchBuffer !== null) {
    logWarn(`Force-resetting batch state (depth was ${batchDepth}, buffer had ${batchBuffer?.length || 0} ops)`);
  }
  batchDepth = 0;
  batchBuffer = null;
}
